import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, ElementRef, ChangeDetectorRef, AfterViewInit, OnDestroy, output, inject, viewChild } from '@angular/core';
import { AuthSevices } from '@fitness-force/api';
import { ClientSubscriptionFreezeStatus, PlanCategory } from '@fitness-force/enum';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { BehaviorSubject, combineLatest, Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '@fitness-force/shared-utils';

@Component({
  selector: 'actions',
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonModule,
    PopoverModule,
    TranslateModule,
    AsyncPipe,
    NgClass
]
})

export class ActionsComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService = inject(AuthSevices);
  cdr = inject(ChangeDetectorRef);
  private featureFlagService = inject(FeatureFlagService);

  columnData: Observable<any> = new BehaviorSubject(null);
  additionData: Observable<any> = new BehaviorSubject(null); 
  rowIndex: number = 0;
  readonly listContainer = viewChild.required<ElementRef>('listContainer');

  readonly onAction = output<{ action: string; columnData: any; rowIndex: number }>();
  showOptions = false;
  currentUserDetails: any;
  showSalesReturn=  new BehaviorSubject(false);
  showRefund= new BehaviorSubject(false);
  showWriteOff= new BehaviorSubject(false);
  isFreezeV2Flow= new BehaviorSubject(false);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.currentUserDetails = this.authService.decodedToken;
    
    // Check all feature flags using combineLatest for cleaner code
    // This waits for Statsig initialization and updates all flags at once
    combineLatest([
      this.featureFlagService.getFlag$('show_salesreturn'),
      this.featureFlagService.getFlag$('show_refund'),
      this.featureFlagService.getFlag$('show_writeoff'),
      this.featureFlagService.getFlag$('show_freeze_v2')
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([showSalesReturn, showRefund, showWriteOff, isFreezeV2Flow]) => {
        this.showSalesReturn.next(showSalesReturn);
        this.showRefund.next(showRefund);
        this.showWriteOff.next(showWriteOff);
        this.isFreezeV2Flow.next(isFreezeV2Flow);
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  executeAction(action: string, columnData: any, overlayElement: any) {
    overlayElement.hide();
    this.onAction.emit({ action, columnData: columnData, rowIndex: this.rowIndex });
  }

  isActive(columnData: any): boolean {
    return (columnData.status && columnData.status.toUpperCase() == 'Active'.toUpperCase()) || columnData.is_active || columnData.isActive || false ;
  }

  isStatusVoid(columnData: any): boolean {
    return columnData.status.toLowerCase() == "void";
  }

  showRefundOption(columnData: any):boolean
  {
    return columnData.status.toLowerCase() != "open" && columnData.status.toLowerCase() != "void" && columnData.status.toLowerCase() != "refunded";
  }

  isRefundStatusVoid(columnData: any): boolean {
    return columnData.refund_isvoid;
  }

  isStatusPartialOrOpen(columnData: any): boolean {
    return columnData.status.toLowerCase() == "partialopen" || columnData.status.toLowerCase() == "open" || columnData.status.toLowerCase() == "notpaid";
  }

  get hasAdminBilling(): boolean {
    return this.currentUserDetails.can_admin_billing == 'true' ? true : false;
  }

  get CanAdminClass(): boolean {
    return this.currentUserDetails.can_admin_class == 'true' ? true : false;
  }

  hasListItems(): boolean {
    const listContainer = this.listContainer();
    return listContainer?.nativeElement?.querySelectorAll('li').length > 0;
  }

  hasBalanceAmount(columnData: any): boolean {
    return columnData.total_balance_due_in_minor_currency && columnData.total_balance_due_in_minor_currency > 0;
  }

  showStopCampaign(columnData: any): boolean {
    return columnData.campaign_status.toLowerCase() == "inprogress";
  }

  showVoidCampaign(columnData: any): boolean {
    return columnData.campaign_status.toLowerCase() == "scheduled";
  }

  isPresales(columnData: any): boolean {
    return columnData.status.toLowerCase() == "presales";
  }

  isInactiveTenant(columnData: any): boolean {
    return columnData.status.toLowerCase() == "inactive" || columnData.status.toLowerCase() == "in-active";
  }

  showActiveTenant(columnData: any): boolean {
    return columnData.status.toLowerCase() == "active";
  }

  isPresalesTenant(columnData: any): boolean {
    return columnData.status.toLowerCase() == "presales";
  }

  ShowEditOption(columnData: any): boolean {
    return (columnData.status == ClientSubscriptionFreezeStatus.ScheduledFreeze || columnData.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.ScheduledFreeze])
    || (columnData.status == ClientSubscriptionFreezeStatus.FreezeInprogress || columnData.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress]);;
  }

  ShowEditOptionInV1ForNonContract(columnData: any): boolean {
    if (this.isFreezeV2Flow.value) {
      return true;
    }
    else if (this.isContract(columnData)) {
      return false;
    }
    return true;
  }

  ShowCancelOption(columnData: any): boolean {
    return columnData.status == ClientSubscriptionFreezeStatus.ScheduledFreeze || columnData.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.ScheduledFreeze];
  }

  ShowStopOption(columnData: any): boolean {
    return columnData.status == ClientSubscriptionFreezeStatus.FreezeInprogress || columnData.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress];
  }

  isContract(columnData: any): boolean {
    return columnData?.client_subscription && ((columnData.client_subscription.category == PlanCategory.Contract || columnData.client_subscription.category.toString() == PlanCategory[PlanCategory.Contract]) || (columnData.client_subscription.category == PlanCategory.PayAsYouGo || columnData.client_subscription.category.toString() == PlanCategory[PlanCategory.PayAsYouGo]));
  }

  isStatusActiveOrInActiveForCampaign(columnData: any): boolean {
    return columnData.campaign_status.toLowerCase() == "active" || columnData.campaign_status.toLowerCase() == "inactive";
  }

  hasVisibleActions(data: any): boolean {
    const row = data.row;
    const adata = data.adata;
    
    if (!adata || !row) return false;

    // Check each action condition to see if at least one would be visible
    const conditions = [
      adata?.includes('edit'),
      adata?.includes('status'),
      adata?.includes('tenantstatus') && !this.isPresalesTenant(row),
      adata?.includes('delete'),
      adata?.includes('print'),
      adata?.includes('sendemail'),
      adata?.includes('sendwhatsapp'),
      adata?.includes('void') && !this.isStatusVoid(row),
      adata?.includes('collectdues') && row.total_balance_due_in_minor_currency > 0,
      adata?.includes('refundandsalesreturn') && this.showSalesReturn.value && !this.isStatusVoid(row),
      adata.includes('changebillowner'),
      adata.includes('cancelTransfer'),
      adata.includes('startTransfer'),
      adata.includes('salesreturnvoid'),
      adata.includes('refundvoid') && !this.isRefundStatusVoid(row),
      adata.includes('refund') && this.showRefund.value && this.showRefundOption(row),
      adata.includes('selfsignup') && this.isActive(row),
      adata.includes('balanceclear') && this.isStatusPartialOrOpen(row),
      adata.includes('changedate') && this.isPresales(row),
      adata.includes('writeoff') && this.showWriteOff.value && this.hasBalanceAmount(row) && !this.isStatusVoid(row),
      adata?.includes('duplicate'),
      adata?.includes('convertContact'),
      adata?.includes('stop_campaign') && this.showStopCampaign(row),
      adata?.includes('void_campaign') && this.showVoidCampaign(row),
      adata?.includes('view'),
      adata?.includes('mark_fulfilled') && row.fulfilled === false,
      adata?.includes('cancel') && (row.status === 'Queued' || row.status === 'InProgress'),
      adata?.includes('edit_freeze') && this.ShowEditOption(row) && this.ShowEditOptionInV1ForNonContract(row), //&& !this.isContract(row),
      adata?.includes('cancel_freeze') && this.ShowCancelOption(row),
      adata?.includes('stop_freeze') && this.ShowStopOption(row) && this.ShowEditOptionInV1ForNonContract(row),
      adata?.includes('campaign_status') && this.isStatusActiveOrInActiveForCampaign(row),
      adata?.includes('create_charge')
    ];

    return conditions.some(condition => condition === true);
  }

}
